import { useEffect, useMemo, useRef } from 'react'
import { useStore } from 'react-redux'
import { FundingProduct } from '@frontend/components/agromarket/core/access-control'
import classNames from 'classnames'
import { useApplyFinancingOffer, useApplyLostFinancingOffer } from '@/components/composed/financing/hooks'
import { useApplyMFOFinancingOffer } from '@/components/composed/financing/hooks/useApplyMFOFinancingOffer'
import { useApplyingOfferFormSteps } from '@/components/composed/landingBlocks/deprecated/LandingApplyingOfferForm/useApplyingOfferFormSteps'
import { openModal } from '@/components/composed/Modal/actions'
import { useAuthenticateUser } from '@/components/ui/AuthButton/hooks'
import { FormValuesFormatter } from '@/constants/financing'
import { isAuthenticated as isUserAuthenticated } from '@/logic/auth/reducer'
import type {
  IApplyingFinancingOfferProps,
  IApplyingFinancingOfferRequestBody,
  IFinancingOfferFormValues,
} from '@/logic/financing/interfaces'
import { useGtm } from '@/logic/metrika/financing/gtmHelpers'
import { getProfile } from '@/logic/profile/reducer'
import type { ELoanProductName } from '@/types'
import { getApplyingFinancingOfferFormInitialValues } from '@/utils/getApplyingFinancingOfferFormInitialValues'
import type { TOpenApplyingFinancingOfferModalHandler } from '@/utils/hooks/applyingFinancingOffer/types'
import { useApplyingFinancingOfferForm } from '@/utils/hooks/applyingFinancingOffer/useApplyingFinancingOfferForm'
import { useRegionsMicrocreditOrganizations } from '@/utils/hooks/applyingFinancingOffer/useRegionsMicrocreditOrganizations'
import { isApplyingOfferRequestBody } from '../typeguard'
import { FINANCING_OFFER_MODAL_CONTAINER_CLASSNAME_BY_OFFER_TYPE, FINANCING_OFFER_MODAL_STEPS_ADAPTER } from './constants'
import { defaultFinancingOfferModalStepsAdapter } from './helpers'
import type { TFormInteractionHandler } from './types'
import './styles.scss'

export const useOpenApplyingFinancingOfferModal = (props: IApplyingFinancingOfferProps) => {
  const { productId, offerType, gtmId, customCommentField } = props

  const { getState } = useStore()
  const { isAuthenticated, openAuthModal } = useAuthenticateUser()
  const { dispatch, setOptions } = useApplyingFinancingOfferForm({ offerType, productId })

  const { isMFOProduct } = useRegionsMicrocreditOrganizations(props)
  const { applyFinancingOffer } = useApplyFinancingOffer()
  const { applyLostFinancingOffer } = useApplyLostFinancingOffer()
  const { applyMFOFianncingOffer } = useApplyMFOFinancingOffer()
  const steps = useApplyingOfferFormSteps(offerType, isAuthenticated)
  const { pushGtmFormView, pushGtmLoanProductModalAuth, pushGtmFormInteraction } = useGtm()

  const onFormInteraction: TFormInteractionHandler = eventLabel => {
    if (gtmId && eventLabel) pushGtmFormInteraction(gtmId, eventLabel)
  }

  const onSubmitFundingRequest = async (formValues: IFinancingOfferFormValues, productName?: ELoanProductName | FundingProduct) => {
    const body = FormValuesFormatter[offerType]?.(formValues) || formValues

    const requestBody: IFinancingOfferFormValues | IApplyingFinancingOfferRequestBody = customCommentField
      ? { ...body, comment: customCommentField }
      : body

    if (!isApplyingOfferRequestBody(requestBody)) return

    const rootState = getState()
    const isAuthenticatedMode = isUserAuthenticated()(rootState)

    if (isMFOProduct) {
      await applyMFOFianncingOffer({
        body: requestBody,
        withSuccessModal: true,
      })
    } else {
      await applyFinancingOffer({ body: requestBody, offerType, withSuccessModal: true })
    }

    if (!isAuthenticatedMode) {
      setOptions({
        formValues: {},
      })

      if (productName) pushGtmLoanProductModalAuth(productName)

      openAuthModal({
        shouldSendCodeImmediately: true,
        initialValues: { phone: formValues.phone, email: formValues.email, role: 'user' },
        onCloseDialog: () => setOptions({ formValues }),
        onCloseModal: () => {
          applyLostFinancingOffer({ body: requestBody, offerType })
        },
        shouldSkipSelectRoleStep: true,
      })
    }
  }

  const formattedSteps = useMemo(
    () => {
      const adapter = FINANCING_OFFER_MODAL_STEPS_ADAPTER[offerType] ?? defaultFinancingOfferModalStepsAdapter
      const offerTypeRelatedSteps = adapter(steps, { dispatch, onFormInteraction })

      return offerTypeRelatedSteps
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [steps],
  )

  const formattedStepsRef = useRef(formattedSteps)

  useEffect(() => {
    formattedStepsRef.current = formattedSteps
  }, [formattedSteps])

  const openApplyingFinancingOfferModal: TOpenApplyingFinancingOfferModalHandler = (productName, initialValues) => {
    if (productName) pushGtmFormView(productName)

    const rootState = getState()
    const profile = getProfile()(rootState)

    if (productId === FundingProduct.SBERPAY) {
      dispatch(openModal({ options: { dialogId: 'FinancingConsultingModal' } }))

      return
    }

    dispatch(
      openModal({
        options: {
          dialogId: 'CustomizedFormModal',
          modalClassName: 'financingOfferModalWrapper',
        },
        contentOptions: {
          className: 'financingOfferModal',
          modalContentClassName: classNames(
            'financingOfferModal__container',
            FINANCING_OFFER_MODAL_CONTAINER_CLASSNAME_BY_OFFER_TYPE[offerType],
          ),
          customizedFormProps: {
            autoFocus: true,
            ignoreValidationForSubmitButton: true,
            onSubmit: (values: IFinancingOfferFormValues) => onSubmitFundingRequest(values, productName),
            initialValues: getApplyingFinancingOfferFormInitialValues({ formValues: initialValues, offerType, productId, profile }),
            steps: formattedStepsRef.current,
            // TODO: убрать хранимое в redux состояние запроса https://jira.poleinvest.ru/browse/IFA-2085
            requestStatusName: 'mockedRequestStatus',
            shouldShowRequiringFieldsLabel: false,
            shouldUseLocalRequestState: true,
          },
        },
      }),
    )
  }

  return { openApplyingFinancingOfferModal }
}
